import React, { useState, useEffect, useCallback, useRef } from 'react';
import { notificationService, getErrorMessage } from '../services/api';
import { 
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  Zap,
  CreditCard,
  AlertTriangle,
  Info,
  Loader2,
  X,
  Key
} from 'lucide-react';
import { toast } from 'sonner';

const NOTIFICATION_ICONS = {
  charging_complete: Zap,
  charging_started: Zap,
  low_balance: CreditCard,
  balance_loaded: CreditCard,
  session_error: AlertTriangle,
  system_alert: Info,
  license_warning: Key
};

const NOTIFICATION_COLORS = {
  charging_complete: 'text-green-600 bg-green-50',
  charging_started: 'text-blue-600 bg-blue-50',
  low_balance: 'text-orange-600 bg-orange-50',
  balance_loaded: 'text-green-600 bg-green-50',
  session_error: 'text-red-600 bg-red-50',
  system_alert: 'text-blue-600 bg-blue-50',
  license_warning: 'text-red-600 bg-red-50'
};

// Native Button Component
const NativeIconButton = ({ onClick, disabled, className = '', children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`inline-flex items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-slate-700 ${className}`}
  >
    {children}
  </button>
);

const NativeSmallButton = ({ onClick, disabled, className = '', children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-slate-700 ${className}`}
  >
    {children}
  </button>
);

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationService.list(false, 20);
      setNotifications(data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationService.getUnreadCount();
      setUnreadCount(data?.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationService.delete(id);
      const notif = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notif && !notif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Az önce';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} saat önce`;
    return date.toLocaleDateString('tr-TR');
  };

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <div className="relative">
      {/* Notification Bell Button - Native */}
      <button 
        ref={buttonRef}
        type="button"
        className="relative p-2 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors"
        onClick={toggleDropdown}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-slate-600 dark:text-slate-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs bg-red-500 text-white rounded-full font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel - No Portal, direct child */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden"
          style={{ maxHeight: 'calc(100vh - 100px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <h3 className="font-semibold text-slate-900 dark:text-white">Bildirimler</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <NativeSmallButton 
                  onClick={handleMarkAllAsRead}
                  disabled={loading}
                  className="text-slate-600 dark:text-slate-300"
                >
                  {loading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <CheckCheck className="h-3 w-3 mr-1" />
                      Tümünü Oku
                    </>
                  )}
                </NativeSmallButton>
              )}
              <NativeIconButton 
                className="h-7 w-7 text-slate-500"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </NativeIconButton>
            </div>
          </div>
          
          {/* Notification List */}
          <div className="max-h-[300px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[200px] text-slate-500 dark:text-slate-400">
                <BellOff className="h-10 w-10 mb-2 text-slate-300 dark:text-slate-600" />
                <p className="text-sm">Bildirim yok</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {notifications.map((notif) => {
                  const Icon = NOTIFICATION_ICONS[notif.type] || Info;
                  const colorClass = NOTIFICATION_COLORS[notif.type] || 'text-gray-600 bg-gray-50';
                  
                  return (
                    <div 
                      key={notif.id}
                      className={`p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                        !notif.is_read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                      }`}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`p-2 rounded-full ${colorClass} flex-shrink-0`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm truncate text-slate-900 dark:text-white">{notif.title}</p>
                            {!notif.is_read && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-slate-400 dark:text-slate-500">
                              {formatTime(notif.created_at)}
                            </span>
                            <div className="flex gap-1">
                              {!notif.is_read && (
                                <NativeIconButton
                                  className="h-6 w-6 text-slate-500"
                                  onClick={() => handleMarkAsRead(notif.id)}
                                  title="Okundu olarak işaretle"
                                >
                                  <Check className="h-3 w-3" />
                                </NativeIconButton>
                              )}
                              <NativeIconButton
                                className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={() => handleDelete(notif.id)}
                                title="Sil"
                              >
                                <Trash2 className="h-3 w-3" />
                              </NativeIconButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
